<template>
  <RevCard class="mb-16 flex-1 p-24 md:mb-0">
    <h2 class="heading-2 mb-8">
      {{ i18n(translations.title) }}
    </h2>
    <div class="text-action-default-low body-1 mb-20">
      {{ i18n(translations.description) }}
    </div>
    <WithSkeletonLoader :is-loading="pending">
      <div v-if="languageCode" class="flex items-center">
        <InputSelectLanguage
          v-model="languageCode"
          class="mr-8 w-full max-w-[18rem]"
        />
        <RevButton
          :loading="isSubmitting"
          variant="secondary"
          @click="handleUpdateLanguage"
          >{{ i18n(translations.updateButton) }}
        </RevButton>
      </div>
    </WithSkeletonLoader>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { ref, watchEffect } from 'vue'

import { type Bcp47Locale, sellerProfileAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'

import InputSelectLanguage from '~/components/Input/SelectLanguage/SelectLanguage.vue'

import WithSkeletonLoader from '../../../../components/WithSkeletonLoader/WithSkeletonLoader.vue'

import translations from './CommunicationLanguage.translations'

const i18n = useI18n()
const { openSuccessToast, openErrorToast } = useTheToast()

const { data, error, pending, refresh } = await useHttpLazyFetch(
  sellerProfileAPI.getProfileViewLanguage,
  {
    queryParams: { views: 'language' },
  },
)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const isSubmitting = ref(false)
const languageCode = ref<Bcp47Locale>()

watchEffect(() => {
  languageCode.value = data.value?.languageCode
})

async function handleUpdateLanguage() {
  isSubmitting.value = true
  try {
    await $httpFetch(sellerProfileAPI.updateProfile, {
      body: { languageCode: languageCode.value },
    })
    refresh()
    openSuccessToast({ title: i18n(translations.languageUpdatedToastSuccess) })
  } catch {
    openErrorToast()
  } finally {
    isSubmitting.value = false
  }
}
</script>
